interface Row {
  label: string;
  value: number;
  /** Resolved CSS color for this entity — reused from the app's badges. */
  color: string;
  /** Where clicking the row navigates. */
  to?: string;
}

/**
 * Magnitude across a handful of named categories — horizontal bars, so the
 * category names read as labels rather than rotated axis ticks.
 *
 * Single series, so there's no legend: the widget title names it and every bar
 * is directly labelled, which also means identity is never carried by color
 * alone. Colors are the entity's own status/priority color from the badge
 * palette, not a chart-specific ramp — the same red that means "critical" on
 * a ticket row means it here. Text stays in ink tokens.
 */
export default function BarBreakdown({ rows, emptyText = 'Nothing to show.' }: {
  rows: Row[];
  emptyText?: string;
}) {
  const total = rows.reduce((a, r) => a + r.value, 0);
  if (rows.length === 0 || total === 0) {
    return <p className="text-[12px] text-muted py-3 text-center">{emptyText}</p>;
  }
  const max = Math.max(...rows.map(r => r.value));

  return (
    <ul className="space-y-2">
      {rows.map((r) => {
        const pct = max > 0 ? Math.max(2, Math.round((r.value / max) * 100)) : 0;
        const share = total > 0 ? Math.round((r.value / total) * 100) : 0;
        const Row = (
          <>
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <span className="text-[12px] text-fg truncate">{r.label}</span>
              <span className="text-[12px] font-semibold text-fg shrink-0 tabular-nums">
                {r.value}
                <span className="text-muted font-normal ml-1">{share}%</span>
              </span>
            </div>
            {/* Track + fill. Thin mark, rounded data-end, anchored left. */}
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--scpa-row-odd)' }}>
              <div
                className="h-full rounded-full transition-[width] duration-300"
                style={{ width: `${pct}%`, background: r.color }}
              />
            </div>
          </>
        );
        return (
          <li key={r.label} title={`${r.label}: ${r.value} of ${total} (${share}%)`}>
            {r.to ? (
              <a href={r.to} className="block hover:opacity-80 transition-opacity">{Row}</a>
            ) : Row}
          </li>
        );
      })}
    </ul>
  );
}
